/**
 * Fills cart summary delivery + bezorgkosten via POST /api/cart/delivery-details.
 */

import { getCart } from '@theme/cart-state';

function readI18n(root) {
  const raw = root.querySelector('script[type="application/json"][data-cart-delivery-i18n]')?.textContent;
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/** Korte datum voor de bezorgregel, bv. "vr 29 mei". */
function formatDeliveryDateLabel(isoYYYYMMD, locale) {
  if (!isoYYYYMMD?.match?.(/^\d{4}-\d{2}-\d{2}$/)) return isoYYYYMMD || '';
  const [y, m, d] = isoYYYYMMD.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return isoYYYYMMD;
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
}

function formatDate(isoYYYYMMD, locale) {
  if (!isoYYYYMMD?.match?.(/^\d{4}-\d{2}-\d{2}$/)) return isoYYYYMMD || '';
  const [y, m, d] = isoYYYYMMD.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return isoYYYYMMD;
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function applyTemplate(template, replacements) {
  if (!template) return '';
  let out = template;
  for (const [key, value] of Object.entries(replacements)) {
    out = out.split(`{${key}}`).join(String(value ?? ''));
  }
  return out;
}

function formatShippingPrice(amount, locale) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

/**
 * @param {HTMLElement} deliveryRoot
 * @param {Record<string, string>} i18n
 * @param {string} locale
 * @param {number | null | undefined} shippingPrice
 */
function updateShippingCostRow(deliveryRoot, i18n, locale, shippingPrice, freeShippingThreshold) {
  const summaryInfo = deliveryRoot.closest('.cart__summary-info');
  const shippingRow = summaryInfo?.querySelector('[data-cart-shipping-cost-row]');

  if (!(shippingRow instanceof HTMLElement)) {
    // Geen bezorgkosten-rij, maar de gratis-bezorging-balk + totaal kunnen er wel zijn.
    updateCheckoutButtonTotal(deliveryRoot, locale, shippingPrice, freeShippingThreshold);
    return;
  }

  const loadingEl = shippingRow.querySelector('[data-cart-shipping-cost-loading]');
  const valueEl = shippingRow.querySelector('[data-cart-shipping-cost-value]');

  if (typeof shippingPrice !== 'number' || Number.isNaN(shippingPrice)) {
    shippingRow.hidden = true;
    updateCheckoutButtonTotal(deliveryRoot, locale, null, freeShippingThreshold);
    return;
  }

  shippingRow.hidden = false;
  if (loadingEl instanceof HTMLElement) loadingEl.hidden = true;
  if (valueEl instanceof HTMLElement) {
    var isFreeShipping = shippingPrice === 0;
    valueEl.textContent = isFreeShipping
      ? i18n.shippingFree || 'Gratis'
      : formatShippingPrice(shippingPrice, locale);
    valueEl.classList.toggle('cart__shipping-cost-value--free', isFreeShipping);
    valueEl.hidden = false;
  }

  updateCheckoutButtonTotal(deliveryRoot, locale, shippingPrice, freeShippingThreshold);
}

/**
 * @param {HTMLElement} deliveryRoot
 * @param {string} locale
 * @param {number | null | undefined} shippingPriceEuro
 * @param {number | null | undefined} freeShippingThreshold - Gratis-bezorggrens (incl. btw, euro's) uit de API
 */
async function updateCheckoutButtonTotal(deliveryRoot, locale, shippingPriceEuro, freeShippingThreshold) {
  const summaryRoot =
    deliveryRoot.closest('.cart-drawer__summary') ||
    deliveryRoot.closest('cart-items-component') ||
    deliveryRoot.closest('.cart__summary-info')?.parentElement;
  if (!(summaryRoot instanceof HTMLElement)) return;

  let cart;
  try {
    cart = await getCart();
  } catch {
    return;
  }
  const subtotalEuro = (Number(cart.total_price) || 0) / 100;

  // Gratis-bezorging voortgangsbalk (onafhankelijk van de totaal-incl-verzending vlag).
  updateFreeShippingBar(summaryRoot, locale, subtotalEuro, freeShippingThreshold);

  // Totaal incl. verzending: de drawer toont het via .cart__total-value (estimated-total-rij),
  // de split-total checkout-knop via .cart__checkout-button-total. Alleen waar de vlag aan staat.
  if (deliveryRoot.dataset.includeShippingInCheckoutTotal === 'true') {
    const totalEls = summaryRoot.querySelectorAll('.cart__checkout-button-total, .cart__total-value');
    if (totalEls.length) {
      const shippingEuro =
        typeof shippingPriceEuro === 'number' && Number.isFinite(shippingPriceEuro) ? shippingPriceEuro : 0;
      const formatted = formatShippingPrice(subtotalEuro + shippingEuro, locale);
      totalEls.forEach((el) => {
        el.textContent = formatted;
        el.setAttribute('value', formatted);
      });
    }
  }
}

/**
 * Leest de i18n-strings van de gratis-bezorging-balk.
 * @param {HTMLElement} bar
 */
function readFreeShippingI18n(bar) {
  const raw = bar.querySelector('script[type="application/json"][data-free-shipping-i18n]')?.textContent;
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/**
 * Werkt de gratis-bezorging voortgangsbalk bij.
 * Effectieve grens: API-waarde wint; anders de interim-fallback uit data-attr.
 * Geen geldige grens (null/undefined/0) => balk verbergen.
 * @param {HTMLElement} summaryRoot
 * @param {string} locale
 * @param {number} subtotalEuro - Subtotaal incl. btw, excl. bezorging
 * @param {number | null | undefined} apiThreshold - Grens uit de API (incl. btw, euro's)
 */
function updateFreeShippingBar(summaryRoot, locale, subtotalEuro, apiThreshold) {
  const bar = summaryRoot.querySelector('[data-free-shipping-bar]');
  if (!(bar instanceof HTMLElement)) return;

  let threshold = null;
  if (typeof apiThreshold === 'number' && Number.isFinite(apiThreshold)) {
    threshold = apiThreshold;
  } else if (apiThreshold === undefined) {
    const attr = Number(bar.dataset.freeShippingThreshold);
    if (Number.isFinite(attr) && attr > 0) threshold = attr;
  }

  if (threshold === null || threshold <= 0) {
    bar.hidden = true;
    return;
  }
  bar.hidden = false;

  const remaining = Math.max(0, threshold - subtotalEuro);
  const qualified = subtotalEuro >= threshold;
  const percent = Math.max(0, Math.min(100, Math.round((subtotalEuro / threshold) * 100)));

  const fill = bar.querySelector('[data-free-shipping-fill]');
  if (fill instanceof HTMLElement) fill.style.width = percent + '%';

  bar.classList.toggle('cart-free-shipping--qualified', qualified);

  const i18n = readFreeShippingI18n(bar);
  const textEl = bar.querySelector('[data-free-shipping-text]');
  if (textEl instanceof HTMLElement) {
    if (qualified) {
      textEl.textContent = i18n.qualified || 'Je hebt gratis bezorging!';
    } else {
      const tmpl = i18n.remaining || 'Nog {amount} tot {phrase}';
      const phrase = i18n.phrase || 'gratis bezorging';
      textEl.innerHTML = applyTemplate(tmpl, {
        amount: '<strong>' + formatShippingPrice(remaining, locale) + '</strong>',
        phrase: '<strong>' + phrase + '</strong>',
      });
    }
  }
}

/**
 * @param {HTMLElement} root
 */
async function initCartDeliveryEstimate(root) {
  if (root.dataset.cartDeliveryInitialized === 'true') return;
  root.dataset.cartDeliveryInitialized = 'true';

  const apiUrl = root.dataset.deliveryApiUrl?.trim();
  if (!apiUrl) {
    root.hidden = true;
    return;
  }

  const shopDomain = root.dataset.shopDomain?.trim();
  const country = root.dataset.destinationCountry?.trim() || 'NL';
  const locale = root.dataset.locale?.trim() || 'nl-NL';
  const i18n = readI18n(root);

  const loadingEl = root.querySelector('[data-cart-delivery-loading]');
  const valueEl = root.querySelector('[data-cart-delivery-value]');
  const popoverBody = root.querySelector('[data-cart-delivery-popover-body]');

  const setPopoverHtml = (text) => {
    if (popoverBody) popoverBody.textContent = text;
  };

  try {
    const cart = await getCart();
    const items = (cart.items || []).map((line) => {
      const qty = Math.max(1, Number(line.quantity) || 1);
      const numericId = String(line.variant_id ?? '');
      const lineCents = line.final_line_price ?? line.line_price ?? 0;
      const unitEuro = lineCents / 100 / qty;
      return {
        mainProduct: true,
        productId: line.product_id != null ? String(line.product_id) : '',
        productVariantNumericId: numericId,
        price: unitEuro,
        quantity: qty,
      };
    });

    if (items.length === 0) {
      root.hidden = true;
      updateShippingCostRow(root, i18n, locale, null);
      return;
    }

    const headers = { 'Content-Type': 'application/json' };
    if (shopDomain) headers['X-Shopify-Shop-Domain'] = shopDomain;

    const apiRes = await fetch(apiUrl, {
      method: 'POST',
      headers,
      credentials: 'omit',
      body: JSON.stringify({
        items,
        destination: { countryIsoAlpha2Code: country },
      }),
    });

    const responseText = await apiRes.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch {
      throw new Error('delivery-details response not JSON');
    }

    if (!apiRes.ok) {
      throw new Error(`delivery-details HTTP ${apiRes.status}`);
    }

    const ship = data.fastest_shipping_date;
    const del = data.fastest_delivery_date;
    const carrierName = data.shippingProviderName || data.carrier_name;

    const dateLabel = formatDeliveryDateLabel(del || ship, locale);
    const summaryText = applyTemplate(i18n.expectedBy || '', { date: dateLabel }) || dateLabel;

    if (valueEl) {
      valueEl.textContent = summaryText;
    }

    if (popoverBody) {
      const shipFmt = formatDate(ship, locale);
      const delFmt = formatDate(del, locale);
      let popText;
      if (carrierName) {
        popText = applyTemplate(i18n.popoverWithCarrier || '', {
          date: delFmt || dateLabel,
          ship_date: shipFmt,
          carrier: carrierName,
        });
      } else {
        popText = applyTemplate(i18n.popoverNoCarrier || '', {
          date: delFmt || dateLabel,
          ship_date: shipFmt,
        });
      }
      setPopoverHtml(
        popText ||
          [delFmt && `Bezorging uiterlijk ${delFmt}`, carrierName && `Vervoerder: ${carrierName}`]
            .filter(Boolean)
            .join(' '),
      );
    }

    if (loadingEl) loadingEl.hidden = true;
    if (valueEl) valueEl.hidden = false;

    const rawShippingPrice = data.shipping_price;
    const shippingPrice =
      rawShippingPrice === null || rawShippingPrice === undefined ? null : Number(rawShippingPrice);

    updateShippingCostRow(
      root,
      i18n,
      locale,
      Number.isFinite(shippingPrice) ? shippingPrice : null,
      data.free_shipping_threshold,
    );
  } catch {
    if (valueEl) {
      valueEl.textContent = i18n.error || '';
      valueEl.hidden = false;
    }
    if (loadingEl) loadingEl.hidden = true;
    setPopoverHtml(i18n.errorDetail || i18n.error || '');
    // Bezorging onbekend; balk valt terug op de interim-grens (data-attr) via undefined.
    updateShippingCostRow(root, i18n, locale, null, undefined);
  }
}

/**
 * @param {ParentNode} [root]
 */
function resetDeliveryEstimateState(root = document) {
  root.querySelectorAll('[data-cart-delivery-estimate]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    delete el.dataset.cartDeliveryInitialized;
    delete el.dataset.cartDeliveryQueued;
  });
}

/**
 * @param {ParentNode} [root]
 */
function initAll(root = document) {
  root.querySelectorAll('[data-cart-delivery-estimate]:not([data-cart-delivery-queued])').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.dataset.cartDeliveryQueued = 'true';
    initCartDeliveryEstimate(el).catch(() => {});
  });
}

/**
 * @returns {HTMLDialogElement | null}
 */
function findLoadedCartDrawerDialog() {
  const drawer = document.querySelector('cart-drawer-component[data-cart-drawer-loaded="true"]');
  if (!(drawer instanceof HTMLElement)) return null;
  const dialog = drawer.querySelector('dialog.cart-drawer__dialog, dialog');
  return dialog instanceof HTMLDialogElement ? dialog : null;
}

/** @param {{ reset?: boolean; root?: ParentNode }} [options] */
function runInitAll(options = {}) {
  const root = options.root || document;
  if (options.reset) resetDeliveryEstimateState(root);
  initAll(root);
}

if (!window.__hdsCartDeliveryEstimateBoot) {
  window.__hdsCartDeliveryEstimateBoot = true;
  /** @type {Window & { hdsInitCartDeliveryEstimates?: typeof runInitAll }} */ (window).hdsInitCartDeliveryEstimates =
    runInitAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => runInitAll(), { once: true });
  } else {
    runInitAll();
  }

  document.addEventListener('cart:update', (event) => {
    if (event instanceof CustomEvent && event.detail?.sourceId === 'cart-drawer-hydrate') {
      const dialog = findLoadedCartDrawerDialog();
      if (dialog) {
        requestAnimationFrame(() => runInitAll({ reset: true, root: dialog }));
      }
      return;
    }
    requestAnimationFrame(() => {
      const dialog = findLoadedCartDrawerDialog();
      runInitAll({ reset: true, root: dialog || document });
    });
  });

  document.addEventListener('dialog:open', (event) => {
    const dialog = event.target instanceof HTMLDialogElement ? event.target : null;
    if (!dialog?.closest('cart-drawer-component')) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => runInitAll({ reset: true, root: dialog }));
    });
  });
}
